import React, {useCallback, useEffect, useMemo, useState} from 'react';

import {yupResolver} from '@hookform/resolvers/yup';
import {useStore} from 'effector-react';
import {Controller, ControllerRenderProps, FieldError, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import * as yup from 'yup';

import {fetchStorages, ProfileStorageLocation} from 'api/documents/create';
import {ReactComponent as CrossIcon} from 'assets/svg/cross.svg';
// import {ReactComponent as TrashIcon} from 'assets/svg/trash.svg';
import {ReactComponent as WarningIcon} from 'assets/svg/warning.svg';
import {BackTo} from 'components/common/BackTo';
import {Button, ButtonVariant} from 'components/ui-kit/Button';
import {Icon} from 'components/ui-kit/Icon';
import {Input} from 'components/ui-kit/Input';
import {Modal} from 'components/ui-kit/Modal';
import {ContentContainer} from 'components/ui-kit/PageComponents';
import {Select, SelectOption} from 'components/ui-kit/Select';
import {Tooltip} from 'components/ui-kit/Tooltip';
import {history} from 'core/history';
import {ROUTE} from 'core/routes';
import {isAdmin$, isCustomsDirector$, permissions$} from 'models/auth';
import {
    $blockUserModal,
    $userStatusChangePending,
    boundUserWithParticipantFx,
    editUserData$,
    onBlockUser,
    onUnblockUser,
    removeUserFx,
    setBlockUserModal,
    updateUserFx,
} from 'models/profile/staff/table';
import {EmployeeRegistrationRequest, EmployeeUpdateRequest} from 'types';

import {UserStatusTag} from '../styled';

import {
    Actions,
    BackButton,
    CancelConfirmButton,
    ConfirmBlockMessage,
    ConfirmDeleteButton,
    ConfirmRemoveFooter,
    ConfirmRemoveHeader,
    ConfirmRemoveHeaderIcon,
    ConfirmRemoveModalContent,
    ConfirmRemoveTitle,
    CustomTitle,
    // DeleteButton,
    Field,
    FormFields,
    ModalCloseButton,
    RemoveConfirmationModal,
    SubmitButton,
    TitleRow,
    WarningMessage,
    Wrapper,
} from './styled';

const additionalStyles = `
    text-align: left;
    max-width: 195px;
    border-radius: 8px;
    opacity: 1 !important;
`;

type FormField = {
    name: keyof EmployeeRegistrationRequest;
    readonly?: boolean;
    options?: SelectOption<string>[];
};

type UpdateUserForm = Partial<EmployeeRegistrationRequest> & {
    first_name: string;
    last_name: string;
    middle_name?: string;
    phone?: string;
    storageLocationId?: number | null;
};

const ConfirmBlock = () => {
    const userStatusChangePending = useStore($userStatusChangePending);
    const blockUserModal = useStore($blockUserModal);
    const {t} = useTranslation();
    const translate = (key: string) => t('pages.profile.staff.confirmBlock.' + key);

    const [reason, setReason] = useState('');
    const [showReason, setShowReason] = useState(false);

    const buttonText = useMemo(() => {
        if (showReason) {
            return blockUserModal?.toBlock ? translate('block') : translate('unblock');
        }

        return translate('ok');
    }, [blockUserModal, translate, t]);

    if (!blockUserModal) return null;

    return (
        <Modal
            title={blockUserModal.toBlock ? translate('title') : translate('titleUnblock')}
            modalWidth={0}
            show={!!blockUserModal?.isOpen}
            closable={!userStatusChangePending}
            onClose={() => setBlockUserModal(null)}
        >
            <ConfirmBlockMessage>
                {blockUserModal.toBlock ? translate('message') : translate('messageUnblock')}
            </ConfirmBlockMessage>
            {showReason && (
                <Input
                    isRequired
                    type="text"
                    label={t('pages.profile.staff.confirmBlock.reason')}
                    placeholder={t('pages.profile.staff.confirmBlock.reasonPlaceholder')}
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    style={{marginBottom: 10}}
                />
            )}
            <ConfirmRemoveFooter>
                <Button
                    disabled={userStatusChangePending}
                    type="button"
                    variant={ButtonVariant.Outline}
                    onClick={() => {
                        setBlockUserModal(null);
                        setShowReason(false);
                    }}
                >
                    {translate('cancel')}
                </Button>
                <Button
                    variant={ButtonVariant.Normal}
                    size="md"
                    type="button"
                    disabled={userStatusChangePending || (showReason && !reason)}
                    onClick={() => {
                        if (showReason) {
                            blockUserModal.toBlock ? onBlockUser(reason) : onUnblockUser(reason);
                        } else {
                            setShowReason(true);
                        }
                    }}
                >
                    {buttonText}
                </Button>
            </ConfirmRemoveFooter>
        </Modal>
    );
};

export const UpdateUser = () => {
    const {t, i18n} = useTranslation();
    const permissions = useStore(permissions$);
    const isAdmin = useStore(isAdmin$);
    const isCustomsDirector = useStore(isCustomsDirector$);

    const [storageLocations, setStorageLocations] = useState<ProfileStorageLocation[]>([]);
    const [showRemoveConfirmation, setShowRemoveConfirmation] = useState(false);
    const translateValidates = useCallback(
        (key: string) => t(`pages.register.validations.${key}`),
        [i18n.language],
    );
    const translateForm = useCallback((key: string) => t('pages.register.fields.' + key), [
        i18n.language,
    ]);

    const editUserData = useStore(editUserData$);
    const canViewUserStorages = isAdmin || isCustomsDirector;
    const storagesUserId = canViewUserStorages ? editUserData?.id : undefined;

    const schema = useMemo(
        () =>
            yup.object().shape({
                first_name: yup.string().required(translateValidates('required')),
                middle_name: yup.string().notRequired(),
                last_name: yup.string().required(translateValidates('required')),
                phone: yup
                    .string()
                    .transform((value) => (value === '' ? null : value))
                    .nullable()
                    .notRequired()
                    .matches(/^\+?\d{10,15}$/, translateValidates('phone')),
                storageLocationId: yup
                    .number()
                    .nullable()
                    .notRequired(),
            }),
        [translateValidates],
    );

    const {
        control,
        formState: {dirtyFields, errors, isDirty},
        handleSubmit,
        reset,
    } = useForm<UpdateUserForm>({
        resolver: yupResolver(schema),
        mode: 'all',
    });

    useEffect(() => {
        if (!editUserData) {
            history.push(ROUTE.STAFF);
            return;
        }

        reset({
            first_name: editUserData.firstName,
            middle_name: editUserData.middleName,
            last_name: editUserData.lastName,
            phone: editUserData.phone,
            storageLocationId: editUserData.clientId ?? null,
        });
    }, [editUserData, reset]);

    useEffect(() => {
        if (canViewUserStorages && storagesUserId == null) return;

        fetchStorages(storagesUserId)
            .then((response) => setStorageLocations(response.storageLocations))
            .catch((error) => console.error(error));
    }, [canViewUserStorages, storagesUserId]);

    const storageLocationOptions = useMemo<SelectOption<number>[]>(
        () =>
            storageLocations.map((item) => ({
                value: item.id,
                label: item.storageName ?? '-',
                isDisabled: item.status === 'deactivated',
            })),
        [storageLocations],
    );

    const canEditUser =
        permissions.includes(permissions.userOwnEdit) ||
        permissions.includes(permissions.userAllEdit);
    const canUpdateUser = canEditUser && !isAdmin;

    const onSubmit = async (data: UpdateUserForm) => {
        try {
            if (!editUserData?.id) return;
            if (!canUpdateUser) return;

            const hasProfileChanges = [
                dirtyFields.first_name,
                dirtyFields.middle_name,
                dirtyFields.last_name,
                dirtyFields.phone,
            ].some(Boolean);
            const hasStorageLocationChange =
                (editUserData.clientId ?? null) !== (data.storageLocationId ?? null);

            if (hasProfileChanges) {
                const payload: EmployeeUpdateRequest & {id: string | number} = {
                    firstName: {value: data.first_name, reset: false},
                    middleName: {value: data.middle_name || '', reset: false},
                    lastName: {value: data.last_name, reset: false},
                    phone: {value: data.phone || '', reset: false},
                    id: editUserData.id,
                };

                await updateUserFx(payload);
            }
            if (hasStorageLocationChange) {
                await boundUserWithParticipantFx({
                    userId: editUserData.id,
                    clientId: data.storageLocationId ?? null,
                });
            }
            toast.success(t('pages.register.message.update'));
            reset(data);
            history.push(ROUTE.STAFF);
        } catch (error) {
            console.log(error);
        }
    };

    const onRemoveUser = async () => {
        if (!editUserData?.id) return;
        if (!canEditUser) return;

        await removeUserFx(editUserData.id);
        setShowRemoveConfirmation(false);
        reset();
        history.push(ROUTE.STAFF);
    };

    const formFields: FormField[] = useMemo(
        () => [{name: 'first_name'}, {name: 'middle_name'}, {name: 'last_name'}, {name: 'phone'}],
        [t],
    );

    const controlRender = (
        controlEvents: ControllerRenderProps<UpdateUserForm>,
        field: FormField,
    ) => {
        const baseProps = {
            placeholder: translateForm(field.name),
            hasError: !!errors?.[field.name],
            disabled: field.readonly,
            readOnly: isAdmin,
        };

        const props = {...baseProps, ...controlEvents};
        return <Input {...props} />;
    };

    // const canRemoveUser = !!editUserData && canEditUser;

    return (
        <>
            <BackTo to={ROUTE.STAFF}>{t('pages.profile.staff.create.back')}</BackTo>
            <ContentContainer>
                <Wrapper>
                    <form onSubmit={handleSubmit(onSubmit)} style={{marginTop: 50}}>
                        <TitleRow>
                            <CustomTitle size={4}>
                                {t('pages.profile.staff.update.title')}
                            </CustomTitle>
                            {editUserData && (
                                <UserStatusTag isActive={!editUserData.isBlocked}>
                                    {editUserData.isBlocked
                                        ? t('dictionaries.profile.staff.status.BLOCKED')
                                        : t('dictionaries.profile.staff.status.ACTIVE')}
                                </UserStatusTag>
                            )}
                        </TitleRow>
                        <FormFields>
                            {formFields.map((field) => (
                                <Field
                                    key={field.name}
                                    text={(errors?.[field.name] as FieldError)?.message}
                                    isError={!!errors?.[field.name]}
                                >
                                    <span>{t(`pages.register.fields.${field.name}`)}</span>
                                    <Controller
                                        name={field.name}
                                        control={control}
                                        defaultValue=""
                                        render={(props) => controlRender(props, field)}
                                    />
                                </Field>
                            ))}
                            <Field>
                                <span style={{marginBottom: 2}}>
                                    {t('pages.profile.staff.storageLocation')}
                                </span>
                                <Controller
                                    name="storageLocationId"
                                    control={control}
                                    defaultValue={null}
                                    render={({value, onChange}) => (
                                        <Select
                                            isClearable
                                            disabled={isAdmin}
                                            value={
                                                storageLocationOptions.find(
                                                    (option) => option.value === value,
                                                ) ?? null
                                            }
                                            options={storageLocationOptions}
                                            onChange={(option: SelectOption<number> | null) =>
                                                onChange(option?.value ?? null)
                                            }
                                        />
                                    )}
                                />
                            </Field>
                        </FormFields>
                        <Actions>
                            {canEditUser && (
                                <div style={{marginRight: 'auto'}}>
                                    {!editUserData?.isBlocked &&
                                        Boolean(editUserData?.clientId) && (
                                            <Tooltip
                                                id={`blockTooltip-${editUserData?.id}`}
                                                place={'left'}
                                                backgroundColor="white"
                                                textColor="#111111"
                                                additionalStyles={
                                                    additionalStyles +
                                                    'box-shadow: 0px 3px 6px rgba(116, 116, 116, 0.3);'
                                                }
                                            />
                                        )}
                                    {(!editUserData?.isBlocked || isAdmin) && (
                                        <Button
                                            data-tip={t(
                                                'pages.profile.staff.confirmBlock.blockButtonTooltip',
                                            )}
                                            data-for={`blockTooltip-${editUserData?.id}`}
                                            disabled={
                                                !editUserData?.isBlocked &&
                                                Boolean(editUserData?.clientId)
                                            }
                                            variant={ButtonVariant.Cancel}
                                            size="sm"
                                            type="button"
                                            onClick={() => {
                                                if (!editUserData?.id) return;
                                                setBlockUserModal({
                                                    isOpen: true,
                                                    userId: editUserData.id,
                                                    isNextStep: false,
                                                    toBlock: !editUserData?.isBlocked,
                                                });
                                            }}
                                        >
                                            {editUserData?.isBlocked
                                                ? t('pages.profile.staff.confirmBlock.unblock')
                                                : t('pages.profile.staff.confirmBlock.block')}
                                        </Button>
                                    )}
                                </div>
                            )}
                            {/*<DeleteButton*/}
                            {/*    type="button"*/}
                            {/*    size="sm"*/}
                            {/*    variant={ButtonVariant.Outline}*/}
                            {/*    disabled={!canRemoveUser}*/}
                            {/*    $alignLeft*/}
                            {/*    icon={*/}
                            {/*        <Icon source={TrashIcon} fillColor="currentColor" width={16} />*/}
                            {/*    }*/}
                            {/*    onClick={() => setShowRemoveConfirmation(true)}*/}
                            {/*>*/}
                            {/*    {t('pages.profile.staff.confirm.ok')}*/}
                            {/*</DeleteButton>*/}
                            <BackButton
                                type="button"
                                size="sm"
                                variant={ButtonVariant.Outline}
                                onClick={() => {
                                    history.push(ROUTE.STAFF);
                                    reset();
                                }}
                            >
                                {t('pages.profile.staff.create.cancel')}
                            </BackButton>
                            <SubmitButton
                                type="submit"
                                size="sm"
                                disabled={!isDirty || !canUpdateUser}
                            >
                                {t('pages.profile.staff.update.updateBtn')}
                            </SubmitButton>
                        </Actions>
                    </form>
                </Wrapper>
            </ContentContainer>
            <RemoveConfirmationModal
                modalWidth={0}
                show={showRemoveConfirmation}
                hideCloseButton
                onClose={() => setShowRemoveConfirmation(false)}
            >
                <ConfirmRemoveModalContent>
                    <ConfirmRemoveHeader>
                        <ConfirmRemoveTitle>
                            <ConfirmRemoveHeaderIcon>
                                <Icon
                                    source={WarningIcon}
                                    strokeColor="#FF3030"
                                    fillColor="transparent"
                                    width={20}
                                    height={20}
                                />
                            </ConfirmRemoveHeaderIcon>
                            {t('pages.profile.staff.confirm.title')}
                        </ConfirmRemoveTitle>
                        <ModalCloseButton
                            type="button"
                            onClick={() => setShowRemoveConfirmation(false)}
                        >
                            <Icon source={CrossIcon} fillColor="#939AA4" width={12} height={12} />
                        </ModalCloseButton>
                    </ConfirmRemoveHeader>
                    <WarningMessage>
                        <p>{t('pages.profile.staff.confirm.deleteWarning')}</p>
                        <p>{t('pages.profile.staff.confirm.deleteWarningUndo')}</p>
                    </WarningMessage>
                </ConfirmRemoveModalContent>
                <ConfirmRemoveFooter>
                    <CancelConfirmButton
                        size="sm"
                        variant={ButtonVariant.Outline}
                        onClick={() => setShowRemoveConfirmation(false)}
                    >
                        {t('pages.profile.staff.confirm.cancel')}
                    </CancelConfirmButton>
                    <ConfirmDeleteButton size="sm" onClick={onRemoveUser}>
                        {t('pages.profile.staff.confirm.delete')}
                    </ConfirmDeleteButton>
                </ConfirmRemoveFooter>
            </RemoveConfirmationModal>
            <ConfirmBlock />
        </>
    );
};
